import React, { Component } from 'react';

class CameraCapture extends Component {
  constructor(props) {
    super(props);

    this.state = {
      stream: null,
      error: null
    };

    this.videoRef = React.createRef();
    this.canvasRef = React.createRef();
  }

  componentDidMount() {
    this.startCamera();
  }

  componentWillUnmount() {
    this.stopCamera();
  }

  startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: 'environment', // Use back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }

      this.setState({ stream });

    } catch (err) {
      console.error('❌ Camera access error:', err);
      this.setState({ 
        error: 'Camera access denied. Please enable camera permissions in your browser settings.' 
      });
    }
  };

  stopCamera = () => {
    if (this.state.stream) {
      this.state.stream.getTracks().forEach(track => track.stop());
    }
  };

  capturePhoto = () => {
    const video = this.videoRef.current;
    const canvas = this.canvasRef.current;

    if (!video || !canvas) return;

    // Calculate dimensions (max 1920x1080 while maintaining aspect ratio)
    let width = video.videoWidth;
    let height = video.videoHeight;
    const maxWidth = 1920;
    const maxHeight = 1080;

    if (width > height) {
      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }
    }

    // Set canvas dimensions to compressed size
    canvas.width = width;
    canvas.height = height;

    // Draw current video frame to canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, width, height);

    // Convert canvas to blob with compression (0.8 quality)
    canvas.toBlob((blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(blob);

      this.props.onCapture({ file, preview });
      this.stopCamera();
    }, 'image/jpeg', 0.8);
  };

  render() {
    if (this.state.error) {
      return (
        <div className="camera-capture" style={styles.container}>
          <div className="alert alert-danger" role="alert">
            <h4>Camera Error</h4>
            <p>{this.state.error}</p>
            <button 
              className="btn btn-primary"
              onClick={this.props.onClose}
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="camera-capture" style={styles.container}>
        {/* Video Preview */}
        <video
          ref={this.videoRef}
          autoPlay
          playsInline
          style={styles.video}
        />

        {/* Hidden canvas for capture */}
        <canvas ref={this.canvasRef} style={{ display: 'none' }} />

        {/* Controls */}
        <div style={styles.controls}>
          <button
            className="btn btn-secondary btn-lg"
            onClick={this.props.onClose}
            style={styles.cancelButton}
          >
            Cancel
          </button>

          <button
            className="btn btn-primary btn-lg"
            onClick={this.capturePhoto}
            style={styles.captureButton}
          >
            <i className="fa-sharp fa-regular fa-camera fa-2x"></i>
          </button>

          <div style={styles.spacer} />
        </div>
      </div>
    );
  }
}

// Inline styles for camera component
const styles = {
  container: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    WebkitTransform: 'translateZ(0)', // Force GPU acceleration on iOS
    transform: 'translateZ(0)'
  },
  video: {
    flex: 1,
    width: '100%',
    objectFit: 'cover',
    WebkitTransform: 'translateZ(0)', // Force GPU acceleration
    transform: 'translateZ(0)'
  },
  controls: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px',
    backgroundColor: 'rgba(0, 0, 0, 0.9)',
    zIndex: 10000,
    WebkitTransform: 'translateZ(0)', // Force GPU acceleration
    transform: 'translateZ(0)',
    minHeight: '120px', // Ensure sufficient height
    flexShrink: 0 // Prevent controls from shrinking
  },
  cancelButton: {
    minWidth: '100px',
    position: 'relative',
    zIndex: 10001
  },
  captureButton: {
    minWidth: '80px',
    minHeight: '80px',
    borderRadius: '50%',
    padding: '10px',
    position: 'relative',
    zIndex: 10001
  },
  spacer: {
    minWidth: '100px'
  }
};

export default CameraCapture;